/**
 * Booking a payment against an invoice, and correcting one already booked.
 *
 * Lifted out of `DocPayments` when that card reached the size at which two
 * responsibilities were sharing one file: the list of what has arrived, and the
 * form for saying that more has. Nothing about the form changed in the move.
 */
import { useState } from "react";
import { Check, Loader2, Plus } from "lucide-react";
import { DatePicker } from "@wa/ui";
import {
  type CrmPaymentMethods,
  defaultPaymentToken,
  enabledPaymentRows,
  isBuiltinPaymentMethod,
  methodToken,
} from "@wa/core/payment-methods";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { addPayment, updatePayment, type Invoice, type Payment } from "../../../lib/sales";
import { Btn, Modal, SelectInput, useToast } from "../../ui";
import { toDateInput, toIso } from "../projects/lib";
import { centsToInput, inputToCents } from "./shared";

/**
 * The two picker values that are not a method.
 *
 * Sentinels rather than an empty string, because an empty string is also what a
 * cleared free-text box holds, and the two mean different things: one is a mode,
 * the other is a value in that mode. Neither can collide with a real option - a
 * token is either a built-in string or a trimmed non-empty label.
 */
const OTHER = "\u0000other";
const NONE = "\u0000none";

/**
 * One form, whether the payment is being booked or corrected.
 *
 * Correcting used to be impossible: the only thing the row menu offered was
 * delete, so a mistyped sum or a wrong date meant removing the row and typing
 * it again. That loses the payment's identity - the audit trail then reads as
 * money deleted and different money arriving - and it is the wrong story about
 * what happened, which was a typo.
 *
 * The date is a field in both modes for the same reason: a payment is entered
 * off a bank statement days after it landed, and defaulting it to today quietly
 * moved every historic transfer to the day it was typed in.
 */
export function PaymentModal({
  invoice,
  remaining,
  payment,
  methods,
  onClose,
  onSaved,
}: {
  invoice: Invoice;
  remaining: number;
  /** Absent when booking a new payment. */
  payment?: Payment;
  /** What Settings > Payments offers. Passed in rather than fetched here: the
   *  card that owns this dialog already has it by the time anybody clicks. */
  methods: CrmPaymentMethods;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const editing = payment != null;

  // A new payment is prefilled with what is still owed, because settling the
  // balance in full is the common case; an existing one opens on what it says.
  const [amount, setAmount] = useState(centsToInput(payment ? payment.amountCents : Math.max(remaining, 0)));

  // The methods this desk offers, and the one this form starts on. A payment
  // being corrected keeps whatever it was booked under - INCLUDING a method
  // since switched off, or one imported from the old CRM. Rewriting it to a
  // currently-offered name would be this dialog changing a fact about the past
  // because a setting moved.
  const offered = enabledPaymentRows(methods);
  // `editing ? ... : ...` and NOT `payment?.method ?? default`. A stored method
  // of null is a real state - the old CRM's importer writes one for any source
  // row with no payment mode - and `??` cannot tell it from "no payment passed".
  // Collapsing the two would preselect the desk's default, and a save made to
  // correct the DATE would quietly tag the payment with a method nobody entered.
  const [method, setMethod] = useState(editing ? (payment.method ?? "") : (defaultPaymentToken(methods) ?? ""));
  /** Does the picker currently offer this exact stored string? */
  const isOffered = (value: string) => value !== "" && offered.some((row) => methodToken(row) === value);

  /**
   * The free-text box, and the ONLY thing that opens it: the operator picking
   * "Other".
   *
   * It used to open on its own whenever the stored method was not on the list,
   * which put a text box in front of anybody correcting the date on an old
   * payment. A method the list no longer offers is drawn as its own option
   * instead (`ownOption` below), so the value is preserved without asking
   * anybody to retype it.
   */
  const [wantsFreeText, setWantsFreeText] = useState(false);

  /**
   * The payment's own method, when the picker does not offer it.
   *
   * A row imported from the old CRM, or one booked under a method since
   * switched off. Drawn as a real option and selected, so leaving the field
   * alone saves exactly what was there. `null` when there is nothing to add -
   * this must never appear for a method the list already has.
   *
   * Derived on every render rather than stored, so a `methods` prop that lands
   * after the dialog opened cannot leave the select displaying one option while
   * the state holds another, which is the one that would then be saved.
   */
  const ownOption = !wantsFreeText && method !== "" && !isOffered(method) ? method : null;

  /**
   * What the select is showing.
   *
   * `NONE` is a real state and not a placeholder: a payment can be stored with
   * no method at all (`Payment.method` is nullable, and the old CRM's importer
   * writes null for any source row with no payment mode). Leaving it alone has
   * to save it as it was.
   */
  const selectValue = wantsFreeText ? OTHER : method === "" ? NONE : method;
  const [reference, setReference] = useState(payment?.reference ?? "");
  // `toDateInput` and not a slice of the ISO string: a payment booked at half
  // past one in the morning stores an instant that is still YESTERDAY in UTC,
  // and slicing would open the dialog on the wrong day and then save it there.
  const [date, setDate] = useState(toDateInput(payment?.paidAt ?? new Date().toISOString()));
  const [busy, setBusy] = useState(false);

  const cents = inputToCents(amount);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (cents <= 0 || busy) return;
    setBusy(true);
    try {
      if (payment) {
        await updatePayment(invoice.id, payment.id, {
          amountCents: cents,
          method: method.trim() || null,
          reference: reference.trim() || null,
          // Only when the field holds a date. An emptied picker means "leave it
          // where it is", never "move this payment to the epoch".
          ...(toIso(date) ? { paidAt: toIso(date) } : {}),
        });
      } else {
        await addPayment(invoice.id, {
          amountCents: cents,
          // Trimmed, like the edit path beside it. Untrimmed, an accidental
          // space made its own bucket in the payments screen's method filter.
          method: method.trim(),
          reference: reference.trim() || undefined,
          ...(toIso(date) ? { paidAt: toIso(date) } : {}),
        });
      }
      onSaved();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = editing ? t("sales.payments.edit") : t("sales.payments.add");

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="crm-lab">{t("sales.payments.amount")}</label>
        <label className="crm-inp">
          <input
            type="number"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="crm-tnum"
            autoComplete="off"
          />
        </label>

        <span className="crm-lab">{t("sales.payments.paidAt")}</span>
        <DatePicker value={date} onChange={setDate} locale={locale} placeholder={t("sales.payments.paidAt")} />

        <label className="crm-lab">{t("sales.payments.method")}</label>
        {/* A picker over what Settings > Payments offers, with a way out at the
            bottom of the list. The escape hatch is not politeness: money that
            has arrived must always be recordable, so a desk that has switched
            every method off, or an operator holding a bank statement with a
            name nobody listed, still has somewhere to type it. It is one
            deliberate choice away, though, and never in the way. */}
        <SelectInput
          value={selectValue}
          label={t("sales.payments.method")}
          onChange={(picked) => {
            if (picked === OTHER) {
              // Cleared, not kept: "none of these" with the previous choice
              // still in the box would read as though it had been chosen.
              setWantsFreeText(true);
              setMethod("");
              return;
            }
            setWantsFreeText(false);
            setMethod(picked === NONE ? "" : picked);
          }}
        >
          {selectValue === NONE ? <option value={NONE}>{t("sales.payments.methodNone")}</option> : null}
          {offered.map((row) => {
            const token = methodToken(row);
            return (
              <option key={row.id} value={token}>
                {isBuiltinPaymentMethod(row.id) ? t(`settings.payments.method.${row.id}`) : token}
              </option>
            );
          })}
          {/* The payment's own method, kept selectable so that correcting a
              date does not retag it. Last before the escape hatch, because it
              is not something this desk offers today. */}
          {ownOption ? <option value={ownOption}>{ownOption}</option> : null}
          <option value={OTHER}>{t("sales.payments.methodOther")}</option>
        </SelectInput>
        {wantsFreeText ? (
          <label className="crm-inp mt-2">
            <input
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              placeholder={t("sales.payments.methodOtherPlaceholder")}
              aria-label={t("sales.payments.methodOther")}
              autoComplete="off"
            />
          </label>
        ) : null}

        <label className="crm-lab">{t("sales.payments.reference")}</label>
        <label className="crm-inp">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t("sales.payments.referencePlaceholder")}
            autoComplete="off"
          />
        </label>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || cents <= 0}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : editing ? (
              <Check className="size-4" />
            ) : (
              <Plus className="size-4" />
            )}
            {editing ? t("common.save") : t("sales.payments.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
